import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Alert, Form, Row, Col, Badge, Spinner, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import PageTitle from '../../layouts/PageTitle';
import {
  Plus,
  Edit2,
  Trash2,
  AlertCircle,
  CheckCircle,
  Building,
  CreditCard,
  FileText,
  Loader2,
  X
} from 'lucide-react';

/**
 * Bank Account Mappings Component
 * Allows users to map real bank accounts (clearing + account number) to chart of accounts
 * This is the "Konton" page in the Bank & Skatt menu
 */
const BankAccountMappings = () => {
  const { companyId } = useParams();

  const [mappings, setMappings] = useState([]);
  const [unmappedAccounts, setUnmappedAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]); // Chart of accounts (payment accounts)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [selectedUnmapped, setSelectedUnmapped] = useState(null);

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch in parallel
      const [mappingsRes, unmappedRes, accountsRes] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings/unmapped`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/accounting/accounts`), { withCredentials: true })
      ]);

      setMappings(mappingsRes.data.mappings || []);
      setUnmappedAccounts(unmappedRes.data.unmappedAccounts || []);

      // Filter to only payment accounts
      const paymentAccounts = (accountsRes.data.accounts || []).filter(acc => acc.is_payment_account === 1);
      setAccounts(paymentAccounts);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Kunde inte hämta kontodata. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMapping = (unmapped = null) => {
    setSelectedUnmapped(unmapped);
    setShowCreateModal(true);
  };

  const handleEditMapping = (mapping) => {
    setSelectedMapping(mapping);
    setShowEditModal(true);
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna mappning?')) {
      return;
    }

    try {
      await axios.delete(
        getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings/${mappingId}`),
        { withCredentials: true }
      );

      setSuccess('Mappning borttagen!');
      fetchData();
    } catch (err) {
      console.error('Error deleting mapping:', err);
      setError('Kunde inte ta bort mappning.');
    }
  };

  const handleApplyMappings = async () => {
    try {
      const res = await axios.post(
        getApiUrl(`${companyId}/accounting/reconciliation/banking/apply-mappings`),
        {},
        { withCredentials: true }
      );

      setSuccess(`Mappningar applicerade på ${res.data.updated} transaktioner!`);
      fetchData();
    } catch (err) {
      console.error('Error applying mappings:', err);
      setError('Kunde inte applicera mappningar.');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Konton" motherMenu="Bank & Skatt" />

      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <AlertCircle size={16} className="me-2" />
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
            <CheckCircle size={16} className="me-2" />
            {success}
          </Alert>
        )}

        {/* Header with actions */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="mb-1">Bankkontomappningar</h3>
            <p className="text-muted mb-0">
              Koppla dina bankkonton till kontoplan för automatisk matchning
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={handleApplyMappings}
              disabled={mappings.length === 0}
            >
              <FileText size={16} className="me-2" />
              Applicera mappningar
            </Button>
            <Button variant="primary" onClick={() => handleCreateMapping()}>
              <Plus size={16} className="me-2" />
              Ny mappning
            </Button>
          </div>
        </div>

        {/* Unmapped Accounts Section */}
        {unmappedAccounts.length > 0 && (
          <Card className="mb-4" style={{ borderLeft: '4px solid #f59e0b' }}>
            <Card.Header style={{ backgroundColor: '#fffbeb', borderBottom: '1px solid #fef3c7' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-1" style={{ color: '#92400e' }}>
                    <AlertCircle size={20} className="me-2" style={{ verticalAlign: 'middle' }} />
                    Omappade bankkonton ({unmappedAccounts.length})
                  </h5>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                    Dessa bankkonton finns i dina transaktioner men har ingen mappning än
                  </p>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))' }}>
                {unmappedAccounts.map((unmapped, idx) => (
                  <div
                    key={idx}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '1rem',
                      backgroundColor: 'white'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="mb-1" style={{ fontSize: '1rem', fontWeight: '600' }}>
                          {unmapped.bank_name || 'Okänd bank'}
                        </h6>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          <CreditCard size={14} className="me-1" style={{ verticalAlign: 'middle' }} />
                          {unmapped.clearing_number}-{unmapped.account_number}
                        </div>
                      </div>
                      <Badge bg="warning" style={{ fontSize: '0.75rem' }}>
                        {unmapped.transaction_count} transaktioner
                      </Badge>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '1rem' }}>
                      {unmapped.first_transaction} till {unmapped.last_transaction}
                    </div>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleCreateMapping(unmapped)}
                      style={{ width: '100%' }}
                    >
                      <Plus size={14} className="me-2" />
                      Skapa mappning
                    </Button>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Active Mappings Section */}
        <Card>
          <Card.Header style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <h5 className="mb-0">
              <CheckCircle size={20} className="me-2" style={{ verticalAlign: 'middle', color: '#16a34a' }} />
              Aktiva mappningar ({mappings.length})
            </h5>
          </Card.Header>
          <Card.Body>
            {mappings.length === 0 ? (
              <div className="text-center py-5">
                <AlertCircle size={48} style={{ color: '#9ca3af', marginBottom: '1rem' }} />
                <h5 style={{ color: '#6b7280' }}>Inga mappningar skapade än</h5>
                <p className="text-muted">
                  Skapa en mappning för att koppla dina bankkonton till kontoplanen
                </p>
                <Button variant="primary" onClick={() => handleCreateMapping()}>
                  <Plus size={16} className="me-2" />
                  Skapa första mappningen
                </Button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))' }}>
                {mappings.map((mapping) => (
                  <div
                    key={mapping.mapping_id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '1.25rem',
                      backgroundColor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="flex-grow-1">
                        <h6 className="mb-2" style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                          {mapping.account_nickname || mapping.accounting_name}
                        </h6>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                          <Building size={14} className="me-2" style={{ verticalAlign: 'middle' }} />
                          {mapping.bank_name || 'Okänd bank'}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          <CreditCard size={14} className="me-2" style={{ verticalAlign: 'middle' }} />
                          {mapping.clearing_number}-{mapping.account_number}
                        </div>
                      </div>
                      <Badge bg="success" style={{ fontSize: '0.75rem' }}>
                        Aktiv
                      </Badge>
                    </div>

                    <div
                      style={{
                        padding: '0.75rem',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '6px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: '500', marginBottom: '0.25rem' }}>
                        Mappas till kontoplan
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#15803d', fontWeight: '600' }}>
                        {mapping.accounting_number} - {mapping.accounting_name}
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleEditMapping(mapping)}
                        style={{ flex: 1 }}
                      >
                        <Edit2 size={14} className="me-2" />
                        Redigera
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDeleteMapping(mapping.mapping_id)}
                        style={{ flex: 1 }}
                      >
                        <Trash2 size={14} className="me-2" />
                        Ta bort
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Create/Edit Modals */}
      <MappingFormModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setSelectedUnmapped(null);
        }}
        accounts={accounts}
        unmapped={selectedUnmapped}
        onSuccess={() => {
          setShowCreateModal(false);
          setSelectedUnmapped(null);
          setSuccess('Mappning skapad!');
          fetchData();
        }}
        companyId={companyId}
      />

      <EditMappingModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedMapping(null);
        }}
        mapping={selectedMapping}
        accounts={accounts}
        onSuccess={() => {
          setShowEditModal(false);
          setSelectedMapping(null);
          setSuccess('Mappning uppdaterad!');
          fetchData();
        }}
        companyId={companyId}
      />
    </>
  );
};

/**
 * Create Mapping Modal Component
 */
const MappingFormModal = ({ show, onHide, accounts, unmapped, onSuccess, companyId }) => {
  const [formData, setFormData] = useState({
    clearingNumber: '',
    accountNumber: '',
    bankName: '',
    accountId: '',
    accountNickname: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (unmapped) {
      setFormData({
        clearingNumber: unmapped.clearing_number || '',
        accountNumber: unmapped.account_number || '',
        bankName: unmapped.bank_name || '',
        accountId: '',
        accountNickname: ''
      });
    } else {
      setFormData({
        clearingNumber: '',
        accountNumber: '',
        bankName: '',
        accountId: '',
        accountNickname: ''
      });
    }
  }, [unmapped, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.post(
        getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings`),
        formData,
        { withCredentials: true }
      );

      onSuccess();
    } catch (err) {
      console.error('Error creating mapping:', err);
      setError(err.response?.data?.message || 'Kunde inte skapa mappning');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Skapa bankkontomappning</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Clearingnummer *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.clearingNumber}
                  onChange={(e) => setFormData({ ...formData, clearingNumber: e.target.value })}
                  required
                  disabled={!!unmapped}
                  placeholder="t.ex. 8327"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Kontonummer *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  required
                  disabled={!!unmapped}
                  placeholder="t.ex. 123456789"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Banknamn</Form.Label>
            <Form.Control
              type="text"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              placeholder="t.ex. Swedbank"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Mappas till kontoplan *</Form.Label>
            <Form.Select
              value={formData.accountId}
              onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
              required
            >
              <option value="">Välj konto...</option>
              {accounts.map(acc => (
                <option key={acc.account_id} value={acc.account_id}>
                  {acc.account_number} - {acc.account_name}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Endast bankkonton (1910-1999) visas här
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Smeknamn (frivilligt)</Form.Label>
            <Form.Control
              type="text"
              value={formData.accountNickname}
              onChange={(e) => setFormData({ ...formData, accountNickname: e.target.value })}
              placeholder="t.ex. Skattekonto, Företagskonto, Sparkonto"
            />
            <Form.Text className="text-muted">
              Ett användarvänligt namn för att identifiera kontot
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            Avbryt
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={16} className="me-2" style={{ animation: 'spin 1s linear infinite' }} />
                Skapar...
              </>
            ) : (
              <>
                <Plus size={16} className="me-2" />
                Skapa mappning
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

/**
 * Edit Mapping Modal Component
 */
const EditMappingModal = ({ show, onHide, mapping, accounts, onSuccess, companyId }) => {
  const [formData, setFormData] = useState({
    bankName: '',
    accountId: '',
    accountNickname: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (mapping) {
      setFormData({
        bankName: mapping.bank_name || '',
        accountId: mapping.account_id || '',
        accountNickname: mapping.account_nickname || ''
      });
    }
  }, [mapping]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.put(
        getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings/${mapping.mapping_id}`),
        formData,
        { withCredentials: true }
      );

      onSuccess();
    } catch (err) {
      console.error('Error updating mapping:', err);
      setError(err.response?.data?.message || 'Kunde inte uppdatera mappning');
    } finally {
      setLoading(false);
    }
  };

  if (!mapping) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Redigera mappning</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          <div className="mb-3 p-3" style={{ backgroundColor: '#f9fafb', borderRadius: '6px' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Bankkonto (kan inte ändras)
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '600' }}>
              {mapping.clearing_number}-{mapping.account_number}
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Banknamn</Form.Label>
            <Form.Control
              type="text"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Mappas till kontoplan *</Form.Label>
            <Form.Select
              value={formData.accountId}
              onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
              required
            >
              {accounts.map(acc => (
                <option key={acc.account_id} value={acc.account_id}>
                  {acc.account_number} - {acc.account_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Smeknamn</Form.Label>
            <Form.Control
              type="text"
              value={formData.accountNickname}
              onChange={(e) => setFormData({ ...formData, accountNickname: e.target.value })}
              placeholder="t.ex. Skattekonto, Företagskonto"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            Avbryt
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={16} className="me-2" style={{ animation: 'spin 1s linear infinite' }} />
                Sparar...
              </>
            ) : (
              <>
                <CheckCircle size={16} className="me-2" />
                Spara ändringar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BankAccountMappings;
